// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SignInButton from "@adl/App/Components/Common/SignInButton";
import ProductActions from "@adl/App/Components/Product/ProductActions";
import ProductGridLayout, { ProductGridLayoutStyle } from "@adl/App/Components/Product/ProductGridLayout";
import productDescriptionStyles from "@adl/App/SharedStyle/ProductDescriptionTextStyles";
import { mergeWithCss, xIsAuthenticated } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    widgetId: string;
    product: ProductModel;
    extendedStyles?: RecommendedProductsGridItemStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
        productSettings: getSettingsCollection(state).productSettings,
    };
};

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecommendedProductsGridItemStyles {
    productGridLayoutStyle?: ProductGridLayoutStyle;
    productImage?: ProductImageStyles;
    descriptionStyles?: ProductDescriptionStyles;
    partNumbersStyles?: ProductPartNumbersStyles;
    priceStyles?: ProductPriceStyles;
    availabilityStyles?: ProductAvailabilityStyles;
    addToListLink?: ProductAddToListLinkStyles;
    signInButton?: ButtonPresentationProps;
}

export const recommendedProductsGridItemStyles: RecommendedProductsGridItemStyles = {
    productGridLayoutStyle: {
        addToCartSection: {
            css: css`
                > * {
                    margin-bottom: 10px;
                }
            `,
        },
        addToListSection: {
            css: css`
                justify-content: flex-end;
            `,
        },
    },
    descriptionStyles: {
        ...productDescriptionStyles("large", {
            productDetailLink: {
                css: css`
                    margin-bottom: 12px;
                `,
            },
        }),
    },
    partNumbersStyles: {
        erpNumberLabelText: {
            weight: "bold",
        },
        manufacturerItemLabelText: {
            weight: "bold",
        },
    },
    priceStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                align-self: flex-end;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    addToListLink: {
        link: {
            typographyProps: {
                size: 12,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 17px;
                `,
            },
        },
    },
    signInButton: {
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
};

const RecommendedProductsGridItem: React.FC<Props> = ({
    product,
    updateProductInfo,
    productInfo,
    extendedStyles,
    widgetId,
    productSettings,
}) => {
    const [styles] = useState(() => mergeWithCss(recommendedProductsGridItemStyles, extendedStyles));
    const { settingsCollection, isAuthenticated } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        settingsCollection: getSettingsCollection(state),
    }));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <ProductGridLayout
                id={`product_${product.id}`}
                data-test-selector="product-carousel-item"
                extendedStyles={styles.productGridLayoutStyle}
            >
                <ProductGridLayout.Image>
                    <ProductImage
                        product={productContext}
                        extendedStyles={styles.productImage}
                        {...{ transformations: { width: 287 } }}
                    />
                </ProductGridLayout.Image>
                <ProductGridLayout.Info>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        showCustomerName={false}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </ProductGridLayout.Info>
                <ProductGridLayout.PriceAndActionsSection>
                    <ProductGridLayout.PriceSection>
                        <ProductPrice
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.priceStyles}
                        />
                    </ProductGridLayout.PriceSection>
                    <ProductGridLayout.AvailabilitySection>
                        {settingsCollection.productSettings.showInventoryAvailability && (
                            <ProductContextAvailability
                                extendedStyles={styles.availabilityStyles}
                                isProductDetailsPage={false}
                            />
                        )}
                    </ProductGridLayout.AvailabilitySection>
                    <ProductGridLayout.AddToCartSection>
                        {!isAuthenticated && <SignInButton extendedStyles={styles.signInButton} />}
                        {isAuthenticated && productContext.product.canAddToCart && (
                            <ProductActions showActions={true} />
                        )}
                    </ProductGridLayout.AddToCartSection>
                    <ProductGridLayout.AddToListAction>
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${product.id}`}
                            extendedStyles={styles.addToListLink}
                        />
                    </ProductGridLayout.AddToListAction>
                </ProductGridLayout.PriceAndActionsSection>
            </ProductGridLayout>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RecommendedProductsGridItem);
